import {FC, useCallback, useEffect} from 'react';
import {makeAutoObservable, runInAction} from 'mobx';
import styles from './RangePicker.module.scss';
import clsx from 'clsx';
import {DxDateBox} from '../DX/DxDateBox';
import DateTimeService from '../../Services/DateTimeService';
import dxDateBox from 'devextreme/ui/date_box';
import {NativeEventInfo} from 'devextreme/events';
import {ValueChangedInfo} from 'devextreme/ui/editor/editor';
import {observer} from 'mobx-react';
import {LocalStorageGet, LocalStorageSet} from "../../Services/Helpers";

type RangePickerProps = {
    onFilterChange: () => void
    rangeStore: RangePickerStore
}
type StoreRange = {
    activeRange: RangeType
    dateTo: string
    dateFrom: string
}

enum RangeType {
    l6h = 'Last 6 hours',
    l24h = 'Last 24 hours',
    l7d = 'Last 7 days',
    l30d = 'Last 30 days',
    l365d = 'Last 365 days',
    custom = 'User defined'
}

const rootClassName = 'rangeSelector';
const FILTER_STORE_KEY = 'rangePicker';

export const RangePicker: FC<RangePickerProps> = observer(({rangeStore, onFilterChange}) => {

    const _onClick = useCallback((e: MouseEvent) => {
        if (rangeStore.visible && !(e.target as Element).closest(`.${rootClassName}`)) {
            rangeStore.hidePicker();
        }
    }, [rangeStore]);

    useEffect(() => {
        document.addEventListener('click', _onClick);
        return () => {
            document.removeEventListener('click', _onClick);
        };
    }, [_onClick]);

    const _renderItem = (range: RangeType) => {
        return <div onClick={rangeStore.setRange(range, onFilterChange)}
                    className={clsx(styles.item, (rangeStore.newRange ? rangeStore.newRange : rangeStore.activeRange) === range && styles.active)}>{range}</div>;
    };
    return (
        <div className={styles.wrapper}>
            <div className={clsx(rootClassName, styles.root, rangeStore.visible && styles.show)}>
                <div className={styles.label} onClick={rangeStore.showPicker}>
                    Selected
                    Range: {rangeStore.activeRange !== RangeType.custom ? rangeStore.activeRange : `${DateTimeService.format(rangeStore.dateFrom, 'DD.MM.YYYY HH:mm')} - ${DateTimeService.format(rangeStore.dateTo, 'DD.MM.YYYY HH:mm')}`}
                </div>
                <div className={styles.popup}>
                    <div className={styles.arrow}/>
                    <div className={styles.body}>
                        <div className={styles.group}>Time Range</div>
                        <div className={styles.list}>
                            {_renderItem(RangeType.l6h)}
                            {_renderItem(RangeType.l24h)}
                            {_renderItem(RangeType.l7d)}
                            {_renderItem(RangeType.l30d)}
                            {_renderItem(RangeType.l365d)}
                            {_renderItem(RangeType.custom)}
                        </div>
                        {((rangeStore.activeRange === RangeType.custom && (!rangeStore.newRange || rangeStore.newRange === RangeType.custom)) || rangeStore.newRange === RangeType.custom) &&
                            <>
                                <div className={styles.group}>Start Time</div>
                                <div className={styles.pickers}>
                                    <DxDateBox
                                        max={rangeStore.dateTo}
                                        value={rangeStore.dateFrom}
                                        type="date"
                                        displayFormat="dd.MM.yyyy"
                                        onValueChanged={rangeStore.onFromChange(onFilterChange)}/>
                                    <DxDateBox
                                        max={rangeStore.dateTo}
                                        value={rangeStore.dateFrom}
                                        type="time"
                                        displayFormat="HH:mm"
                                        onValueChanged={rangeStore.onFromChange(onFilterChange)}/>
                                </div>
                                <div className={styles.group}>End Time</div>
                                <div className={styles.pickers}>
                                    <DxDateBox
                                        min={rangeStore.dateFrom}
                                        value={rangeStore.dateTo}
                                        type="date"
                                        displayFormat="dd.MM.yyyy"
                                        onValueChanged={rangeStore.onToChange(onFilterChange)}/>
                                    <DxDateBox
                                        min={rangeStore.dateFrom}
                                        value={rangeStore.dateTo}
                                        type="time"
                                        displayFormat="HH:mm"
                                        onValueChanged={rangeStore.onToChange(onFilterChange)}/>
                                </div>
                            </>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
});

export class RangePickerStore {
    public activeRange: RangeType = RangeType.l6h;
    public newRange: RangeType | null = null;
    public visible: boolean = false;
    public dateTo: Date = DateTimeService.now();
    public dateFrom: Date = DateTimeService.addTime(this.dateTo, -6);

    constructor() {
        makeAutoObservable(this);
        const hasValue = LocalStorageGet<StoreRange>(FILTER_STORE_KEY);
        if (hasValue) {
            this.dateTo = DateTimeService.fromString(hasValue.dateTo)
            this.dateFrom = DateTimeService.fromString(hasValue.dateFrom)
            this.activeRange = hasValue.activeRange
        }
    }

    public hidePicker() {
        this.visible = false;
    }

    public handleChange() {
        if (this.newRange && this.newRange !== this.activeRange) {
            this.activeRange = this.newRange;
            this.newRange = null;
        }
    }

    private _storeValue(onFilterChange: () => void) {
        LocalStorageSet(FILTER_STORE_KEY, {
            dateTo: this.dateTo.toJSON(),
            dateFrom: this.dateFrom.toJSON(),
            activeRange: this.activeRange
        });
        onFilterChange()
    }

    private _validateChange(onFilterChange: () => void) {
        if (DateTimeService.isValidDate(this.dateFrom) && DateTimeService.isValidDate(this.dateTo) && DateTimeService.compare(this.dateTo, this.dateFrom) > 0) {
            this._storeValue(onFilterChange);
        }
    }

    public onFromChange = (onFilterChange: () => void) => (event: NativeEventInfo<dxDateBox> & ValueChangedInfo) => {
        event.event?.stopPropagation();
        this.dateFrom = event.value || '';
        this._validateChange(onFilterChange);
    };

    public onToChange = (onFilterChange: () => void) => (event: NativeEventInfo<dxDateBox> & ValueChangedInfo) => {
        event.event?.stopPropagation();
        this.dateTo = event.value || '';
        this._validateChange(onFilterChange);
    };

    public setRange = (range: RangeType, onFilterChange: () => void) => () => {
        runInAction(() => {
            this.newRange = range;
            this.dateTo = DateTimeService.now();
            switch (range) {
                case RangeType.l6h:
                    this.dateFrom = DateTimeService.addTime(this.dateTo, -6);
                    break;
                case RangeType.l24h:
                    this.dateFrom = DateTimeService.addDays(this.dateTo, -1);
                    break;
                case RangeType.l7d:
                    this.dateFrom = DateTimeService.addDays(this.dateTo, -7);
                    break;
                case RangeType.l30d:
                    this.dateFrom = DateTimeService.addDays(this.dateTo, -30);
                    break;
                case RangeType.l365d:
                    this.dateFrom = DateTimeService.addDays(this.dateTo, -365);
                    break;
            }
            this.handleChange();
            if (range !== RangeType.custom) {
                this._storeValue(onFilterChange);
            }
        });
    };

    public showPicker = () => {
        this.visible = !this.visible;
    };
}


